import React, { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import * as XLSX from 'xlsx'
import theme from '../styles/theme'
import { addMarianaTekTag, removeMarianaTekTag, lookupMarianaTekUser } from '../services/api'

const TAG_CATALOG: Record<number, string> = {
  1: 'Employee - Personal Account',
  3: 'Extra Care',
  4: 'Injury',
  5: 'Military',
  6: 'Student',
  11: 'HQ Employee',
  12: 'Originals',
  13: 'Mariana Tek Employee',
  14: 'Partner',
  15: 'Notes',
  16: 'First Responder',
  17: 'Needs COVID-19 Waiver',
  50: 'Silver',
  51: 'Cancellation Forgiveness',
  52: 'Free Fuel Item',
  53: 'Black',
  54: 'Member',
  55: 'Red',
  56: 'Platinum',
  83: 'Soho House',
  84: 'Vaccinated',
  149: '5% Fuel Item Discount',
  150: '10% Fuel Item Discount',
  151: 'Early Booking',
  152: 'Priority Waitlist',
  182: '5% Corporate UK',
  183: '10% Corporate UK',
  184: '15% Corporate UK',
  185: '20% Corporate UK',
  215: 'Exclusive Member',
  248: 'Fuel Client Info',
  281: 'Needs Guardian',
  315: 'Hell Week Finisher',
  348: '6 Month Member',
  349: 'First Timer Referred Friend',
}

// Lower-cased name -> tag id, so CSV can use any casing.
const NAME_TO_ID: Record<string, number> = Object.entries(TAG_CATALOG).reduce(
  (acc, [id, name]) => {
    acc[name.toLowerCase()] = Number(id)
    return acc
  },
  {} as Record<string, number>,
)

type Action = 'add' | 'remove'
type RowStatus = 'pending' | 'processing' | 'success' | 'skipped' | 'duplicate' | 'error'

interface ParsedRow {
  email: string
  tagInput: string
  tagId: number | null
  tagName: string | null
  mtUserId: string | null
  status: RowStatus
  message: string
}

const TriggerPill = styled.button`
  height: 40px;
  padding: 0 1.25rem;
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  z-index: ${theme.zIndex.navigation};
  box-shadow: 0 2px 8px ${theme.color.shadow.dark};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #b8002b;
    transform: translateY(-1px);
  }
`

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 94%;
  max-width: 1080px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #eee;
`

const Title = styled.h2`
  font-size: 1.7rem;
  margin: 0;
  color: ${theme.color.text.base};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.9rem;
  cursor: pointer;
  color: ${theme.color.text.grey};
`

const Body = styled.div`
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  color: ${theme.color.text.base};
`

const HelpText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: ${theme.color.text.grey};
  line-height: 1.5;
`

const ActionToggle = styled.div`
  display: flex;
  gap: 0.5rem;
`

const ActionPill = styled.button<{ active: boolean }>`
  padding: 0.5rem 1.25rem;
  border-radius: 18px;
  border: 1px solid ${(p) => (p.active ? theme.color.accent.primary : '#ddd')};
  background: ${(p) => (p.active ? theme.color.accent.primary : 'white')};
  color: ${(p) => (p.active ? 'white' : theme.color.text.base)};
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
`

const Row = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`

const PrimaryButton = styled.button`
  padding: 0.55rem 1.1rem;
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;

  &:disabled {
    background: #c8c8c8;
    cursor: not-allowed;
  }
`

const SecondaryButton = styled.button`
  padding: 0.55rem 1.1rem;
  background: white;
  color: ${theme.color.text.base};
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
`

const FileInput = styled.input`
  font-size: 0.95rem;
`

const TagKeyBox = styled.details`
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  background: #fafafa;

  > summary {
    cursor: pointer;
    font-weight: 600;
    padding: 0.25rem 0;
  }
`

const TagKeyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.25rem 1rem;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: ${theme.color.text.base};
`

const TagKeyRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px dashed #eee;
  padding: 0.15rem 0;
`

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th,
  td {
    text-align: left;
    padding: 0.4rem 0.6rem;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }

  th {
    background: #f4f4f4;
    font-weight: 600;
  }
`

const StatusPill = styled.span<{ s: RowStatus }>`
  display: inline-block;
  padding: 0.1rem 0.55rem;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${(p) =>
    p.s === 'success'
      ? '#d4edda'
      : p.s === 'error'
      ? '#f8d7da'
      : p.s === 'skipped'
      ? '#fff3cd'
      : p.s === 'duplicate'
      ? '#ece6f7'
      : p.s === 'processing'
      ? '#cce5ff'
      : '#e9ecef'};
  color: ${(p) =>
    p.s === 'success'
      ? '#155724'
      : p.s === 'error'
      ? '#721c24'
      : p.s === 'skipped'
      ? '#856404'
      : p.s === 'duplicate'
      ? '#5a2ca0'
      : p.s === 'processing'
      ? '#004085'
      : '#495057'};
`

const Progress = styled.div`
  height: 8px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
`
const ProgressFill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${(p) => p.pct}%;
  background: ${theme.color.accent.primary};
  transition: width 0.2s ease;
`

const Summary = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  font-size: 0.95rem;
  color: ${theme.color.text.base};
`

const downloadTemplateCsv = () => {
  const csv = 'email,tag\nuser1@barrys.com,Originals\nuser2@barrys.com,Black\nuser3@barrys.com,Hell Week Finisher\n'
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'mt-bulk-tags-template.csv'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const resolveTag = (raw: string): { id: number | null; name: string | null } => {
  const t = (raw || '').trim()
  if (!t) return { id: null, name: null }
  // Allow numeric tag IDs too.
  if (/^\d+$/.test(t)) {
    const id = Number(t)
    return TAG_CATALOG[id] ? { id, name: TAG_CATALOG[id] } : { id: null, name: null }
  }
  const id = NAME_TO_ID[t.toLowerCase()]
  return id ? { id, name: TAG_CATALOG[id] } : { id: null, name: null }
}

const parseFile = async (file: File): Promise<ParsedRow[]> => {
  const buf = await file.arrayBuffer()
  const wb = XLSX.read(buf, { type: 'array' })
  const sheet = wb.Sheets[wb.SheetNames[0]]
  const rows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' })

  return rows
    .map((r) => {
      const emailKey = Object.keys(r).find((k) => k.toLowerCase().trim() === 'email')
      const tagKey = Object.keys(r).find((k) => {
        const lc = k.toLowerCase().trim()
        return lc === 'tag' || lc === 'tag_name' || lc === 'tag name'
      })
      const email = String(emailKey ? r[emailKey] : '').trim().toLowerCase()
      const tagInput = String(tagKey ? r[tagKey] : '').trim()
      const { id, name } = resolveTag(tagInput)
      return {
        email,
        tagInput,
        tagId: id,
        tagName: name,
        mtUserId: null,
        status: 'pending' as RowStatus,
        message: '',
      }
    })
    .filter((r) => r.email || r.tagInput)
}

const BulkTagsButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [action, setAction] = useState<Action>('add')
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [running, setRunning] = useState(false)
  const [fileName, setFileName] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  const summary = useMemo(() => {
    const total = rows.length
    const success = rows.filter((r) => r.status === 'success').length
    const errors = rows.filter((r) => r.status === 'error').length
    const skipped = rows.filter((r) => r.status === 'skipped').length
    const duplicates = rows.filter((r) => r.status === 'duplicate').length
    const processing = rows.filter((r) => r.status === 'processing').length
    const done = success + errors + skipped + duplicates
    const pct = total ? Math.round((done / total) * 100) : 0
    return { total, success, errors, skipped, duplicates, processing, done, pct }
  }, [rows])

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    setFileName(f.name)
    try {
      const parsed = await parseFile(f)
      // Pre-validate
      const validated = parsed.map((r) => {
        if (!r.email) return { ...r, status: 'error' as RowStatus, message: 'Missing email' }
        if (!r.tagInput) return { ...r, status: 'error' as RowStatus, message: 'Missing tag' }
        if (!r.tagId) return { ...r, status: 'error' as RowStatus, message: `Unknown tag "${r.tagInput}"` }
        return r
      })
      setRows(validated)
    } catch (err: any) {
      setRows([])
      alert(`Failed to parse file: ${err?.message || err}`)
    } finally {
      // Reset input so picking the same file again triggers change.
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const updateRow = (index: number, patch: Partial<ParsedRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }

  const run = async () => {
    if (running) return
    setRunning(true)
    try {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i]
        if (r.status === 'error') continue // pre-validation already failed
        updateRow(i, { status: 'processing', message: '' })

        // Resolve email -> MT user id
        let mtId: string | null = r.mtUserId
        if (!mtId) {
          try {
            const res = await lookupMarianaTekUser(r.email)
            const matches = (res?.data || []).filter(
              (u: any) =>
                (u?.attributes?.email || '').toLowerCase() === r.email.toLowerCase(),
            )
            if (matches.length === 0) {
              updateRow(i, { status: 'error', message: 'No MT user with that email' })
              continue
            }
            if (matches.length > 1) {
              updateRow(i, { status: 'error', message: `Ambiguous: ${matches.length} MT users share that email` })
              continue
            }
            mtId = String(matches[0].id)
          } catch (err: any) {
            updateRow(i, { status: 'error', message: `Lookup failed: ${err?.message || err}` })
            continue
          }
        }

        try {
          if (action === 'add') await addMarianaTekTag(mtId!, r.tagId!)
          else await removeMarianaTekTag(mtId!, r.tagId!)
          updateRow(i, {
            status: 'success',
            mtUserId: mtId,
            message: `${action === 'add' ? 'Added' : 'Removed'} ${r.tagName}`,
          })
        } catch (err: any) {
          // MT returns 422 when the user already has the tag (add) or doesn't have it (remove).
          // Surface that as a friendly "duplicate" row, not a hard error.
          if (err?.status === 422) {
            const friendly = action === 'add' ? 'Tag already exists on user' : 'Tag not present on user'
            updateRow(i, { status: 'duplicate', mtUserId: mtId, message: friendly })
          } else {
            updateRow(i, { status: 'error', mtUserId: mtId, message: err?.message || String(err) })
          }
        }
      }
    } finally {
      setRunning(false)
    }
  }

  const clear = () => {
    setRows([])
    setFileName('')
  }

  const downloadResults = () => {
    const ws = XLSX.utils.json_to_sheet(
      rows.map((r) => ({
        email: r.email,
        tag: r.tagInput,
        resolved_tag_id: r.tagId ?? '',
        resolved_tag_name: r.tagName ?? '',
        mt_user_id: r.mtUserId ?? '',
        action,
        status: r.status,
        message: r.message,
      })),
    )
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Results')
    XLSX.writeFile(wb, `mt-bulk-tags-results-${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  const sortedCatalog = useMemo(
    () =>
      Object.entries(TAG_CATALOG)
        .map(([id, name]) => ({ id: Number(id), name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [],
  )

  return (
    <>
      <TriggerPill onClick={() => setIsOpen(true)}>Bulk MT Tags</TriggerPill>
      {isOpen && (
        <ModalOverlay onClick={() => !running && setIsOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <Header>
              <Title>Bulk Mariana Tek Tag Manager</Title>
              <CloseButton onClick={() => !running && setIsOpen(false)}>×</CloseButton>
            </Header>
            <Body>
              <HelpText>
                Upload a CSV or Excel file with two columns: <b>email</b> and <b>tag</b>. The tag can be the
                tag name (case-insensitive) or the numeric tag id. Choose <b>Add Tags</b> or <b>Remove Tags</b>;
                the action applies to every row in the file.
              </HelpText>

              <Row>
                <SectionTitle>Action:</SectionTitle>
                <ActionToggle>
                  <ActionPill active={action === 'add'} onClick={() => setAction('add')} disabled={running}>
                    Add Tags
                  </ActionPill>
                  <ActionPill active={action === 'remove'} onClick={() => setAction('remove')} disabled={running}>
                    Remove Tags
                  </ActionPill>
                </ActionToggle>
              </Row>

              <TagKeyBox>
                <summary>Tag Key ({sortedCatalog.length} tags) — click to expand</summary>
                <TagKeyGrid>
                  {sortedCatalog.map((t) => (
                    <TagKeyRow key={t.id}>
                      <span>{t.name}</span>
                      <span style={{ color: '#888' }}>#{t.id}</span>
                    </TagKeyRow>
                  ))}
                </TagKeyGrid>
              </TagKeyBox>

              <Row>
                <SecondaryButton onClick={downloadTemplateCsv}>Download CSV Template</SecondaryButton>
                <FileInput
                  ref={inputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={onFileChange}
                  disabled={running}
                />
                {fileName && <span style={{ color: '#666', fontSize: '0.9rem' }}>{fileName}</span>}
              </Row>

              {rows.length > 0 && (
                <>
                  <Summary>
                    <span>Total: {summary.total}</span>
                    <span>Success: {summary.success}</span>
                    <span style={{ color: '#5a2ca0' }}>Already exists: {summary.duplicates}</span>
                    <span>Skipped: {summary.skipped}</span>
                    <span>Errors: {summary.errors}</span>
                    {running && <span>Processing: {summary.processing}</span>}
                  </Summary>
                  <Progress>
                    <ProgressFill pct={summary.pct} />
                  </Progress>

                  <Row>
                    <PrimaryButton onClick={run} disabled={running || summary.total === 0}>
                      {running ? 'Running...' : `Run ${action === 'add' ? 'Add' : 'Remove'} on ${summary.total} rows`}
                    </PrimaryButton>
                    <SecondaryButton onClick={clear} disabled={running}>
                      Clear
                    </SecondaryButton>
                    {summary.done > 0 && (
                      <SecondaryButton onClick={downloadResults} disabled={running}>
                        Download Results
                      </SecondaryButton>
                    )}
                  </Row>

                  <div style={{ maxHeight: '40vh', overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
                    <PreviewTable>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Email</th>
                          <th>Tag</th>
                          <th>Resolved</th>
                          <th>MT ID</th>
                          <th>Status</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{r.email}</td>
                            <td>{r.tagInput}</td>
                            <td>{r.tagName ? `${r.tagName} (#${r.tagId})` : '—'}</td>
                            <td>{r.mtUserId || '—'}</td>
                            <td>
                              <StatusPill s={r.status}>{r.status}</StatusPill>
                            </td>
                            <td style={{ color: r.status === 'error' ? '#a00' : r.status === 'duplicate' ? '#5a2ca0' : '#444' }}>{r.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </PreviewTable>
                  </div>
                </>
              )}
            </Body>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  )
}

export default BulkTagsButton
